
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, effect, inject, input } from '@angular/core';

@Component({
    selector: 'ev-circular-progress',
    templateUrl: './ev-circular-progress.component.html',
    styleUrls: ['./ev-circular-progress.component.scss'],
    imports: [],
    changeDetection: ChangeDetectionStrategy.OnPush
})
/**
 * Circular progress component that supports both single value and multiple segments.
 * Can be used to display progress, percentages, or pie chart-like visualizations.
 *
 * @example Basic Usage with Single Value
 * ```html
 * <ev-circular-progress [value]="75">
 *   <span>75%</span>
 * </ev-circular-progress>
 * ```
 *
 * @example Multiple Segments with Custom Colors
 * ```html
 * <ev-circular-progress
 *   [segments]="[
 *     { value: 30, color: '#3D91C6' },
 *     { value: 45, color: '#4CAF50' },
 *     { value: 25, color: '#FFC107' }
 *   ]"
 *   size="300px">
 *   <div>Total: 100%</div>
 * </ev-circular-progress>
 * ```
 *
 * @example Pie Chart with Multiple Segments
 * ```html
 * <ev-circular-progress
 *   [segments]="[
 *     { value: 30, color: '#3D91C6' },
 *     { value: 45, color: '#4CAF50' },
 *     { value: 25, color: '#FFC107' }
 *   ]"
 *   [strokeWidth]="50"
 *   size="300px">
 *   <div>Total: 100%</div>
 * </ev-circular-progress>
 * ```
 *
 * @remarks
 * - When using segments, values don't need to sum to 100
 * - Setting maxValue shows remaining space in background color
 * - Content projection allows for custom center content
 * - Animations can be disabled for performance optimization
 *
 * @see {@link Segment} - For segment interface definition
 * @see {@link ISegment} - For internal segment representation
 */
export class EvCircularProgressComponent {
    /**
     * Array of segments to display in the circular progress
     * @param segments Array of objects containing value and optional color
     * @example
     * ```html
     * <ev-circular-progress [segments]="[
     *   { value: 30, color: '#3D91C6' },
     *   { value: 20, color: '#D0D9E0' }
     * ]">
     * ```
     */
    readonly segments = input<Segment[]>([]);

    /**
     * Maximum value for the progress circle, calculates the fill percentage of each segment
     * @param maxValue When set, remaining space will be filled with background color
     * @default 100
     * @example
     * ```html
     * <ev-circular-progress [value]="75" [maxValue]="200">
     * ```
     *
     * ```html
     * <ev-circular-progress [segments]="[{ value: 75 }]">
     * ```
     */
    readonly maxValue = input<number>(100);

    /**
     * Gap between segments in pixels
     * @param segmentGap Size of gap between multiple segments
     * @default 1
     * @remarks Only applies when multiple segments are present
     */
    readonly segmentGap = input<number>(1);

    /**
    * Width of the progress stroke in % of the diameter
    * @param strokeWidth A pie chart is 50
    * @default 15
    * @see maxStrokeWidth For the computed maximum value
    * @example Pie Chart with Multiple Segments
    * ```html
    *  <ev-circular-progress
    *   [segments]="[{ value: 30, color: '#3D91C6' },{ value: 45, color: '#4CAF50' },{ value: 25, color: '#FFC107' }]"
    *   [strokeWidth]="50"
    *   size="300px">
    *   <div>Total: 100%</div>
    * </ev-circular-progress>
    * ```
     */
    readonly strokeWidth = input<number>(15);

    /**
     * Duration of the progress animation in milliseconds
     * @param transitionTime Only applies when animate is true
     * @default 800
     * @see animate
     */
    readonly transitionTime = input<number>(800);

    /**
     * Whether to animate the progress
     * @param animate Set to false to disable animations
     * @default true
     * @remarks Animation uses setTimeout with transitionTime
     */
    readonly animate = input<boolean>(true);

    /**
     * Set a specific size of the circular progress component
     * @param size Any valid CSS size value
     * @default '100%'
     * @example
     * ```html
     * <ev-circular-progress size="'200px'">
     * ```
     */
    readonly size = input<string>('100%');

    /**
     * Single value for a simple progress display
     * @param value Alternative to using segments array
     * @default 0
     * @remarks Only used when segments array is empty
     * @example
     * ```html
     * <ev-circular-progress [value]="42">
     * ```
     */
    readonly value = input<number>(0);

    /**
     * Color for single value progress. Accepts a single color or a two-element tuple for a gradient along the arc.
     * @param strokeColor A CSS color string, or [startColor, endColor] for a gradient
     * @default '#3D91C6' (defaultStrokeColor)
     * @remarks Only used with single value, not segments
     * @example
     * ```html
     * <ev-circular-progress strokeColor="#3D91C6">
     * <ev-circular-progress [strokeColor]="['#a8cfe4', '#3D91C6']">
     * ```
     */
    readonly strokeColor = input<string | string[]>();

    protected strokes: ISegment[] = [];

    protected _segments = computed(() => {
        if (this.segments()?.length > 0) {
            return this.segments();
        }

        if (this.value() !== undefined && this.value() !== null) {
            return [{
                value: this.value(),
                color: this.strokeColor() || this.defaultStrokeColor
            }];
        }

        return [];
    });

    protected maxStrokeWidth = computed(() => Math.min(50, this.strokeWidth()));

    // Radius in viewBox user units (viewBox is 0 0 100 100). Must be numeric:
    // calc() is not valid in the SVG r attribute and throws in Safari.
    protected radius = computed(() => 50 - this.maxStrokeWidth() / 2);

    private readonly defaultStrokeColor: string = '#3D91C6'; //blue20
    private readonly defaultBGColor: string = '#D0D9E0'; //grey12
    private readonly sizeSalt: number = 0.05; //salted size, to avoid segment gaps due to rounding/rendering
    private readonly startOffset: number = 25; // Starting offset for segments for positioning
    private readonly instanceId: string = Math.random().toString(36).slice(2, 8);

    private readonly cdr = inject(ChangeDetectorRef);

    constructor() {

        effect(() => this.updateCircularProgress());
    }

    /**
     * Calculates and updates the circular progress segments
     * Handles animations if enabled
     */
    private updateCircularProgress() {
        let _strokes = [];

        const filteredSegments = [...this._segments()].filter(s => s.value > 0);
        let segmentGap = this.segmentGap();

        let offset = this.startOffset - segmentGap / 2;

        const sumSegmentUnits = filteredSegments.reduce((total, segment) => total + segment.value, 0);
        let totalUnits = sumSegmentUnits;

        if (this.maxValue() != null) {
            const segmentColor = this.defaultBGColor;
            const remainingUnits = this.maxValue() - sumSegmentUnits;

            if (remainingUnits > 0) {
                totalUnits = this.maxValue();
                filteredSegments.push({ value: remainingUnits, color: segmentColor });
            }
        }

        if (filteredSegments.length === 1) {
            segmentGap = 0;
        }

        // Calculate segment sizes and colors and sets starting offset for the dasharray segment
        for (let i = 0; i < filteredSegments.length; i++) {
            let segmentSize = (filteredSegments[i].value / totalUnits) * 100 - segmentGap + this.sizeSalt;
            segmentSize = Math.max(Math.min(100, segmentSize), this.sizeSalt);

            const rawColor = filteredSegments[i].color;
            const segmentColor = Array.isArray(rawColor) ? rawColor[0] : (rawColor || this.defaultStrokeColor);
            const colorEnd = Array.isArray(rawColor) ? rawColor[1] : undefined;

            let dashArray = segmentSize + ',' + (100 - segmentSize);

            const stroke: ISegment = { dashArray: dashArray, offset: offset, stroke: segmentColor, transitionTimeMs: 0, value: filteredSegments[i].value };

            if (colorEnd) {
                const numericRadius = this.radius();
                const startAngleRad = offset * 3.6 * Math.PI / 180;
                const endAngleRad = (offset - segmentSize) * 3.6 * Math.PI / 180;
                const x1 = 50 + numericRadius * Math.cos(startAngleRad);
                const y1 = 50 - numericRadius * Math.sin(startAngleRad);
                const x2 = 50 + numericRadius * Math.cos(endAngleRad);
                const y2 = 50 - numericRadius * Math.sin(endAngleRad);
                const chordLenSq = (x2 - x1) ** 2 + (y2 - y1) ** 2;

                stroke.gradientId = `ev-cp-grad-${this.instanceId}-${i}`;
                stroke.colorStart = segmentColor;
                stroke.colorEnd = colorEnd;

                if (chordLenSq < 4) {
                    // Near-full-circle: chord is degenerate — use vertical top→bottom gradient
                    stroke.gradientX1 = 50;
                    stroke.gradientY1 = 50 - numericRadius;
                    stroke.gradientX2 = 50;
                    stroke.gradientY2 = 50 + numericRadius;
                } else {
                    stroke.gradientX1 = x1;
                    stroke.gradientY1 = y1;
                    stroke.gradientX2 = x2;
                    stroke.gradientY2 = y2;
                }
            }

            _strokes.push(stroke);

            offset = offset - segmentSize - segmentGap + this.sizeSalt;
        }

        // Sets delay for the animations to simulate a "continuing" progress effect
        // Resets the segments then adds them in a continuing flow after timeout
        if (this.animate() == true) {
            let delay = 0
            const resetDashArray = '0, 100';

            for (let i = 0; i < _strokes.length; i++) {
                const transitionTimeMs = Number((this.transitionTime() * _strokes[i].value / totalUnits).toFixed(3));
                const originalDashArray = _strokes[i].dashArray;

                _strokes[i].transitionTimeMs = transitionTimeMs;
                _strokes[i].dashArray = resetDashArray;

                setTimeout(() => {
                    _strokes[i].dashArray = originalDashArray;
                    this.cdr.markForCheck();
                }, delay);

                delay += transitionTimeMs;
            }
        }

        this.strokes = _strokes;
    }
}

export interface Segment {
    value: number;
    /** A CSS color string for solid fill, or [startColor, endColor] for a gradient along the arc. */
    color?: string | string[];
}

interface ISegment {
    dashArray: string;
    offset: number;
    stroke: string;
    transitionTimeMs?: number;
    value?: number;
    gradientId?: string;
    gradientX1?: number;
    gradientY1?: number;
    gradientX2?: number;
    gradientY2?: number;
    colorStart?: string;
    colorEnd?: string;
}
